import { COLORS, Text, Tooltip } from "@asayinc/component-library";
import {
  Box,
  Chip,
  Stack,
  TooltipProps,
  styled,
  tooltipClasses,
} from "@mui/material";
import { toast } from "react-toastify";
import { Security } from "src/store/types";

export const SecurityDisplayField = {
  CUSIP: "cusip",
  TICKER: "ticker",
} as const;

type Props = {
  securities: Security[];
  displayField?: (typeof SecurityDisplayField)[keyof typeof SecurityDisplayField];
};

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    sx={{ display: "inline-grid" }}
  />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
  },
});

export function SecuritiesTagList({
  securities,
  displayField = SecurityDisplayField.CUSIP,
}: Props) {
  const copyValue = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    toast.info(`Copied ${field.toUpperCase()}`, { autoClose: 1000 });
  };

  if (securities.length === 0) {
    return <Text variant="body2">-</Text>;
  }

  const firstSecurity = securities[0];
  const displayValue =
    displayField === SecurityDisplayField.TICKER
      ? firstSecurity.ticker
      : firstSecurity.cusip;

  if (!displayValue) {
    return <Text variant="body2">-</Text>;
  }

  const shouldDisplayTooltip =
    securities.length > 1 || firstSecurity.issuerName;

  return (
    <Box>
      <NoMaxWidthTooltip
        placement="top"
        title={
          shouldDisplayTooltip && (
            <Stack>
              {securities.map(({ id, cusip, ticker, issuerName }) => {
                const value =
                  displayField === SecurityDisplayField.TICKER ? ticker : cusip;
                if (!value) return null;
                return (
                  <Text
                    variant="body2"
                    component="span"
                    sx={{
                      color: COLORS.WHITE,
                      "&:hover": {
                        textDecoration: "underline",
                        cursor: "pointer",
                      },
                    }}
                    onClick={(e) => {
                      copyValue(value, displayField);
                      e.stopPropagation();
                    }}
                    key={id}
                  >
                    {value}
                    {issuerName && " · "}
                    {issuerName}
                  </Text>
                );
              })}
            </Stack>
          )
        }
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip
            label={displayValue}
            onClick={(e) => {
              copyValue(displayValue, displayField);
              e.stopPropagation();
            }}
            size="small"
            sx={{ cursor: "pointer" }}
          />
          {securities.length > 1 && (
            <Text variant="body3">{`+${securities.length - 1}`}</Text>
          )}
        </Stack>
      </NoMaxWidthTooltip>
    </Box>
  );
}
